import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Container, Message } from "semantic-ui-react";
import { Link } from "react-router-dom";
import get from "lodash.get";

import { organizationActions } from "../../pages/organization/store/organizationActions";
import * as generalActions from "../../modules/general";
import { refreshSession, isReadOnly, pingSession } from "../../modules/session";
import { FavoriteHeart } from "../navigation/FavoriteHeart";
import { FavoritesProvider } from "../navigation/FavoritesContext";
import { ReadOnlyContext } from "../common/grid/ReadOnlyContext";

import { Record } from "../../modules/bin/utility";
import LocalStorage from "../../helpers/localStorageHelper";
import { hasGiftAidStatus } from '../../helpers/orgInfoCache';
import { isOnboardingSearch } from '../../pages/organization/edit/onboardingQuery';
import GiftAidNudge from './giftAidNudge/giftAidNudge';

import { Navigation } from "../navigation";
import GeneralErrorModal from "./generalError";
import SessionErrorModal from "./sessionError";
import SessionVerificationModal from "./sessionVerification";
import Spinner from "../common/spinner";

import { localStorageKeys } from "../../constants/localStorageKeys";
import { grecaptcha } from "../../helpers/grecaptcha";

class General extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sidebarMinimized: false // Will be updated by Sidebar component via onToggle
    };
  } componentDidMount() {
    // Don"t initialize from sessionStorage here - let Sidebar component be the single source of truth
    // The Sidebar will call handleSidebarToggle during its initialization to sync the state
  }

  needLoadOrgInfo = (paymentInfo) => {
    return (
      this.props.orgId &&
      this.props.session.isOrganizationAdmin &&
      // A cache written before giftAidStatus existed is refetched once; the
      // reducer always writes the field, so this cannot loop.
      (!paymentInfo ||
        !this.isPaymentInfoOrgIdCorrect(paymentInfo) ||
        !hasGiftAidStatus(paymentInfo))
    );
  };

  isPaymentInfoOrgIdCorrect = (paymentInfo) => {
    const paymentInfoOrgId = paymentInfo && paymentInfo.orgId;

    return paymentInfoOrgId === this.props.orgId;
  };

  showSpinner = () => {
    return (
      Record.isRecordLoading(this.props.session.logOutRecord) ||
      Record.isRecordLoading(this.props.generalOrgRecord)
    );
  };
  /**
   * A 401 sets unauthorized for two quite different reasons: the session is dead, or it is alive
   * but confined behind the MFA or password-reset wall, which refuses everything outside a small
   * allow-list by design. The two look identical from here, so the app used to assume the first
   * and show "your session expired" over a working session until the next ping, up to fifteen
   * minutes later.
   *
   * One ping settles it. Ping is inside the walled garden's allow-list, so a confined session
   * still gets a truthful answer: it succeeds, clears unauthorized, and brings back the session
   * fields that raise the right wall. A genuinely dead session fails and the error modal stands.
   *
   * Only when there is no refresh code. With one, SessionErrorModal already re-establishes the
   * session and that covers the same ground.
   */
  componentDidUpdate(prevProps) {
    if (!this.props.unauthorized) {
      // Reset once resolved, so a later 401 gets its own attempt.
      this.sessionRecheckAttempted = false;
      return;
    }

    // A wall already up means the session has told us why, and a ping adds nothing.
    if (this.props.forceMfa || this.props.requiresPasswordReset) {
      return;
    }

    // With a refresh code, SessionErrorModal already re-establishes the session itself and that
    // brings the same fields back. Pinging as well would be two calls for one answer.
    if (get(this.props.session, 'refresh')) {
      return;
    }

    if (prevProps.unauthorized || this.sessionRecheckAttempted) {
      return;
    }

    this.sessionRecheckAttempted = true;
    grecaptcha(this.props.pingSession, 'SessionOperation');
  }

  handleSessionRefresh = (accountId, code) => {
    const tokenCallback = (token) => this.props.refreshSession(accountId, code, token);
    grecaptcha(tokenCallback, "SessionRefresh");
  };

  render() {
    if (this.showSpinner()) {
      return <Spinner />;
    }

    const paymentInfo = LocalStorage.getItem(localStorageKeys.PAYMENT_INFO);

    if (this.needLoadOrgInfo(paymentInfo)) {
      this.props.getOrganizationRecord(this.props.orgId);
    }

    const isNotConnected = get(paymentInfo, "isConnected") === false;

    // The URL flag covers the first render, before the wizard has set itself up and taken
    // the flag out of the address bar. This layout sits outside the routes' router props.
    const isOrganizationOnboarding =
      this.props.isOrganizationOnboarding ||
      isOnboardingSearch(window.location.search);

    const readOnly = isReadOnly(this.props.session);

    return (
      <ReadOnlyContext.Provider value={readOnly}>
      <FavoritesProvider currentUserId={this.props.currentUserId}>
        <div>
          <SessionErrorModal
            show={
              !this.props.verification &&
              !this.props.forceMfa &&
              !this.props.requiresPasswordReset &&
              (this.props.unauthorized || !this.props.isAdmin)
            }
            session={this.props.session}
            onSessionRefresh={this.handleSessionRefresh}
          />
          <SessionVerificationModal show={this.props.verification} />
          <GeneralErrorModal errors={this.props.errors} />

          <Navigation>
            {(this.props.currentUserId && this.props.isSysAdmin) && (
              <div
                style={{
                  position: "absolute",
                  top: "2rem",
                  right: "2rem",
                  zIndex: 1000
                }}
              >
                <FavoriteHeart
                  placement="page"
                />
              </div>
            )}

            {/* The onboarding wizard ends on the finance step, so it covers this itself. */}
            {this.isPaymentInfoOrgIdCorrect(paymentInfo) &&
              this.props.session.isOrganizationAdmin &&
              isNotConnected &&
              !isOrganizationOnboarding && (
                <Message
                  info
                  className="record-result"
                  attached="bottom"
                >
                  To create a campaign you must{' '}
                  <Link
                    push
                    to={`/organizations/edit/${this.props.orgId}/finance`}
                  >
                    link a payment gateway
                  </Link>
                </Message>
              )
            }
            <GiftAidNudge />
            {this.props.children}
          </Navigation>
        </div>
      </FavoritesProvider>
      </ReadOnlyContext.Provider>
    );
  }
}

const mapState = ({ general, session, accountSettings, templateBuilderEx }) => {
  const orgId = get(session, "organization.id");
  const currentUserId = get(session, "account.id");

  return {
    errors: (general && general.errorsStateful) || [],
    unauthorized: general.unauthorized,
    verification: general.verification,
    forceMfa: accountSettings?.MFA?.forceMfa,
    // A confined session has calls refused by design, which reads as an expired session to the
    // error modal. Same reason forceMfa suppresses it: the wall in front already explains why.
    requiresPasswordReset: session?.requiresPasswordReset,
    session,
    orgId,
    currentUserId,
    generalOrgRecord: general.generalOrgRecord,
    isAdmin: session.isAdmin,
    isSysAdmin: session.isSystemAdmin,
    // Set by the organisation edit page for ?onboarding=1 and cleared when it unmounts.
    isOrganizationOnboarding: templateBuilderEx?.data?.isOnboarding === true,
  };
};

const mapDispatch = (dispatch) => {
  return {
    ...bindActionCreators(generalActions, dispatch),
    pingSession(verificationToken = null) {
      dispatch(pingSession(verificationToken));
    },
    refreshSession(accountId, code, verificationToken = null) {
      dispatch(refreshSession(accountId, code, verificationToken));
    },
    getOrganizationRecord(id) {
      dispatch(
        organizationActions.getOrganizationRecord(
          id,
          generalActions.GET_ORGANIZATION_REQUESTED,
          generalActions.GET_ORGANIZATION_SUCCESS,
          generalActions.GET_ORGANIZATION_FAILURE,
          "generalOrgRecord"
        )
      );
    }
  };
};

export default connect(mapState, mapDispatch)(General);
